import {useEffect, useRef, useState} from 'react';

import {useApiWrite} from '../../../../hooks/useApiWrite';
import {Post} from '../../../../lib/api/POST/Post';
import {PostRoutes} from '../../../../lib/api/POST/PostRoutes';

export const OTP_LENGTH = 6;
export const OTP_MAX_ATTEMPTS = 3;
export const OTP_RESEND_COOLDOWN_SECONDS = 30;

/**
 *
 * A wrong code is a 422. Expired / max-tries are 401s. The API puts the AUTH_OTP_* token in
 * `extra_details` (e.g. "Unauthorized - AUTH_OTP_EXPIRED"), not in `error_code`, which stays a
 * generic unauthorized / unprocessable code.
 */
const OTP_EXPIRED_ERROR_CODE = 'AUTH_OTP_EXPIRED';
const OTP_MAX_TRIES_ERROR_CODE = 'AUTH_OTP_MAX_TRIES_EXCEEDED';

const errorPayloadIncludes = (error: {error_code?: string; extra_details?: string} | undefined, code: string) => {
  const haystack = `${error?.error_code ?? ''} ${error?.extra_details ?? ''}`;

  return haystack.includes(code);
};

export type OtpErrorKey = 'invalidCode' | 'expired' | 'requestFailed' | 'locked';

interface UseOtpVerificationArgs {
  /**
   *
   * The token from the sign-in response. The session cookie is not written until the code
   * validates, so both OTP calls have to authenticate with it explicitly.
   */
  authToken: string;
  onVerified: () => void;
}

export const useOtpVerification = ({authToken, onVerified}: UseOtpVerificationArgs) => {
  const [attemptsLeft, setAttemptsLeft] = useState(OTP_MAX_ATTEMPTS);
  const [isLocked, setIsLocked] = useState(false);
  const [error, setError] = useState<OtpErrorKey>();
  const [codeResent, setCodeResent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown === 0) return;

    const timeout = setTimeout(() => setResendCooldown((seconds) => seconds - 1), 1000);

    return () => clearTimeout(timeout);
  }, [resendCooldown]);

  const isResend = useRef(false);

  const {makeRequest: sendCode, isLoading: isSendingCode} = useApiWrite<Post.CreateOtp>({
    method: 'post',
    path: PostRoutes[Post.CreateOtp],
    authToken,
    disableLoadingOverlay: true,
    onSuccess: () => {
      // A new code resets the server-side attempt counter, so ours has to follow.
      setAttemptsLeft(OTP_MAX_ATTEMPTS);
      setIsLocked(false);
      setError(undefined);
      setCodeResent(isResend.current);
      setResendCooldown(OTP_RESEND_COOLDOWN_SECONDS);
    },
    onError: () => setError('requestFailed'),
  });

  const {makeRequest: submitCode, isLoading: isValidating} = useApiWrite<Post.ValidateOtp>({
    method: 'post',
    path: PostRoutes[Post.ValidateOtp],
    authToken,
    disableLoadingOverlay: true,
    onSuccess: onVerified,
    onError: (apiError) => {
      setCodeResent(false);

      if (errorPayloadIncludes(apiError, OTP_MAX_TRIES_ERROR_CODE)) {
        setAttemptsLeft(0);
        setIsLocked(true);
        setError('locked');

        return;
      }

      if (errorPayloadIncludes(apiError, OTP_EXPIRED_ERROR_CODE)) {
        // Expiry is not the user's fault and costs them no attempt: a new code is the only way
        // forward, so drop the cooldown and let them ask for one right away.
        setResendCooldown(0);
        setError('expired');

        return;
      }

      // The server locks us out on the next call anyway; counting down here saves a round trip.
      const remainingAttempts = Math.max(attemptsLeft - 1, 0);

      setAttemptsLeft(remainingAttempts);
      setIsLocked(remainingAttempts === 0);
      setError(remainingAttempts === 0 ? 'locked' : 'invalidCode');
    },
  });

  const hasRequestedInitialCode = useRef(false);

  useEffect(() => {
    // Guarded rather than left to run on every mount: StrictMode mounts twice in development and
    // the second code would silently invalidate the first.
    if (hasRequestedInitialCode.current) return;

    hasRequestedInitialCode.current = true;
    sendCode();
  }, [sendCode]);

  const resendCode = () => {
    isResend.current = true;
    setCodeResent(false);
    sendCode();
  };

  const validateCode = (code: string) => {
    setError(undefined);
    setCodeResent(false);
    submitCode({otp: code});
  };

  return {
    attemptsLeft,
    codeResent,
    error,
    isLocked,
    isSendingCode,
    isValidating,
    resendCode,
    resendCooldown,
    validateCode,
  };
};
